import { Bell, Check, Clock, X, ExternalLink, RefreshCw } from "lucide-react";
import { useNotifications } from "@/hooks/useNotifications";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import { forceRefresh } from "@/lib/buildVersion";

interface VersionNotification {
  id: "version-update";
  title: string;
  body: string;
  created_at: string;
}

function useVersionNotification() {
  const [versionNotif, setVersionNotif] = useState<VersionNotification | null>(null);

  useEffect(() => {
    const handler = () => {
      setVersionNotif({
        id: "version-update",
        title: "Nouvelle version disponible",
        body: "Cliquez ici pour mettre à jour l'application.",
        created_at: new Date().toISOString(),
      });
    };
    window.addEventListener("new-version-available", handler);
    return () => window.removeEventListener("new-version-available", handler);
  }, []);

  const dismiss = useCallback(() => setVersionNotif(null), []);

  return { versionNotif, dismiss };
}

const NotificationBell = () => {
  const { notifications, unreadCount, markRead, markAllRead, snooze } = useNotifications();
  const navigate = useNavigate();
  const { versionNotif, dismiss: dismissVersion } = useVersionNotification();

  const totalUnread = unreadCount + (versionNotif ? 1 : 0);

  const handleNotificationClick = (n: (typeof notifications)[0]) => {
    const meta = n.meta as Record<string, unknown> | null;
    if (n.type === "program_saved" && meta?.program_id) {
      navigate(`/programs`);
      markRead(n.id);
    }
  };

  const handleVersionClick = async () => {
    dismissVersion();
    await forceRefresh();
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="relative p-2 rounded-lg hover:bg-secondary transition-colors">
          <Bell className={cn("h-5 w-5", totalUnread > 0 ? "text-primary" : "text-muted-foreground")} />
          {totalUnread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground animate-pulse">
              {totalUnread > 9 ? "9+" : totalUnread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <span className="font-semibold text-sm text-foreground">Notifications</span>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" className="text-xs h-7" onClick={markAllRead}>
              <Check className="h-3 w-3 mr-1" /> Tout lire
            </Button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {/* Version update notification — always on top */}
          {versionNotif && (
            <div
              className="border-b border-amber-500/30 px-4 py-3 bg-amber-500/10 cursor-pointer hover:bg-amber-500/20 transition-colors"
              onClick={handleVersionClick}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <RefreshCw className="h-4 w-4 text-amber-400 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-amber-300">{versionNotif.title}</p>
                    <p className="text-xs text-amber-400/80 mt-0.5">{versionNotif.body}</p>
                  </div>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); dismissVersion(); }}
                  className="p-1 rounded hover:bg-secondary"
                >
                  <X className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              </div>
            </div>
          )}

          {notifications.length === 0 && !versionNotif ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Aucune notification</div>
          ) : (
            notifications.map((n) => {
              const hasLink = n.type === "program_saved" && (n.meta as any)?.program_id;
              return (
                <div
                  key={n.id}
                  className={cn(
                    "border-b border-border/50 px-4 py-3 transition-colors",
                    hasLink ? "cursor-pointer hover:bg-primary/5" : "hover:bg-secondary/50"
                  )}
                  onClick={hasLink ? () => handleNotificationClick(n) : undefined}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{n.title}</p>
                      {n.body && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>}
                      {hasLink && (
                        <span className="inline-flex items-center gap-1 text-[10px] text-primary font-semibold mt-1">
                          <ExternalLink className="h-2.5 w-2.5" />
                          Voir dans la bibliothèque
                        </span>
                      )}
                      <span className="text-[10px] text-muted-foreground mt-1 block">
                        {formatDistanceToNow(new Date(n.created_at), { addSuffix: true, locale: fr })}
                      </span>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button
                        onClick={(e) => { e.stopPropagation(); snooze(n.id, 24); }}
                        title="Plus tard (24h)"
                        className="p-1 rounded hover:bg-secondary"
                      >
                        <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); markRead(n.id); }}
                        title="Marquer comme lu"
                        className="p-1 rounded hover:bg-secondary"
                      >
                        <X className="h-3.5 w-3.5 text-muted-foreground" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
